import type { User } from "firebase/auth";
import type { NavigateFunction } from "react-router-dom";
import { mintRedirectToken } from "./callables";
import { calculateReturnUrl, returnUrlAudience } from "./return-url";
import { isProfileComplete, readContactProfile } from "./profile";

/**
 * Single source of truth for "where do we send the user once they're authenticated?".
 * Used by /login, /signup and /verify-email so all three branches agree on:
 *
 *   1. Verified users with a `return` to a TVM audience get an SSO custom token.
 *   2. Anyone else navigates to the in-platform target (`calculateReturnUrl`).
 *
 * Before either, a user who hasn't told us their company and phone goes to
 * /complete-profile (params preserved), which calls back in here with
 * `skipProfileCheck` once they're saved. Accounts from before that screen
 * existed are asked on their next login too.
 *
 * Email verification is NOT gated here. Only fresh signups route through
 * /verify-email (explicitly, from signup.tsx), so the trial can be claimed.
 * Subsequent logins are always allowed — unverified users land on /account
 * and simply have no trial entitlement until they verify on their own.
 */
export async function decideAndRedirect(opts: {
  user: User;
  params: URLSearchParams;
  nav: NavigateFunction;
  onSsoError: () => void;
  skipProfileCheck?: boolean;
}): Promise<void> {
  const { user, params, nav, onSsoError } = opts;

  if (!opts.skipProfileCheck) {
    let complete = true;
    try {
      complete = isProfileComplete(await readContactProfile(user.uid));
    } catch (e) {
      // Not knowing must not lock anyone out; the license request asks again.
      console.warn("[post-auth] could not read profile, skipping the check", e);
    }
    if (!complete) {
      const q = params.toString();
      nav(`/complete-profile${q ? "?" + q : ""}`, { replace: true });
      return;
    }
  }

  const explicitReturn = params.get("return");

  const audience = explicitReturn ? returnUrlAudience(explicitReturn) : null;
  if (audience && explicitReturn) {
    try {
      // Force-refresh ID token so server-set claims are hydrated pre-mint.
      await user.getIdToken(true);
      const { data } = await mintRedirectToken({ audience });
      const sep = explicitReturn.includes("?") ? "&" : "?";
      window.location.href = `${explicitReturn}${sep}token=${encodeURIComponent(data.customToken)}`;
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      // Backend throws HttpsError("failed-precondition", "email-not-verified") when
      // the user hasn't clicked the verification link yet. Route them to /verify-email
      // (preserving `return` and any plan params) so the SSO flow resumes automatically
      // after verification — `/verify-email` calls decideAndRedirect on success.
      if (msg.includes("email-not-verified")) {
        console.info("[post-auth] mint blocked on unverified email — redirecting to /verify-email");
        nav(`/verify-email?${params.toString()}`, { replace: true });
        return;
      }
      console.error("[post-auth] mintRedirectToken failed", e);
      onSsoError();
    }
    return;
  }

  const ret = calculateReturnUrl({
    explicitReturn,
    plan: params.get("plan"),
    interval: params.get("interval"),
  });
  nav(ret, { replace: true });
}
